import React, { useEffect, useRef, useState } from 'react';
import SmartAd from './SmartAd';
import config from '../../config/config';
import { AdSlot } from './types';
import { useSubscriptionPermissions } from '../../features/subscription/hooks/useSubscriptionData';

interface InlineAdProps {
  className?: string;
  slot?: AdSlot;
}

const SLOT_MIN_HEIGHT: Record<AdSlot, number> = {
  banner: 120,
  sidebar: 300,
  inContent: 400,
  footer: 250,
};

const InlineAd: React.FC<InlineAdProps> = ({
  className = '',
  slot = 'inContent',
}) => {
  // Don't render if ads are disabled in config or hidden by the user's subscription
  const adsEnabled = config.ads.provider === 'adsense'
    ? config.ads.adsense.enabled
    : config.ads.ezoic.enabled;

  // Respect user's subscription permission: if adsHidden === true, never render ads
  const { permissions } = useSubscriptionPermissions();
  const userAdsHidden = permissions?.adsHidden === true;

  // Hooks must be called unconditionally. We evaluate `adsEnabled` above,
  // but only return early after hooks are declared to satisfy the Rules of Hooks.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    // If an iframe is already present, mark loaded
    if (el.querySelector('iframe')) {
      setLoaded(true);
      return undefined;
    }

    // Use a more efficient approach: check periodically instead of observing every mutation
    // This reduces performance impact and avoids conflicts with other observers
    let attempts = 0;
    const maxAttempts = 20; // 20 attempts * 200ms = 4 seconds
    
    const checkForAd = () => {
      if (el.querySelector('iframe')) {
        setLoaded(true);
        return;
      }
      
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(checkForAd, 200);
      }
    };

    // Start checking after a brief delay to let AdSense initialize
    const initialTimer = setTimeout(checkForAd, 500);

    return () => {
      clearTimeout(initialTimer);
    };
  }, []);

  if (!adsEnabled) return null;

  if (userAdsHidden) return null;

  const minH = config.ads.testMode ? Math.max(120, SLOT_MIN_HEIGHT[slot]) : SLOT_MIN_HEIGHT[slot];

  return (
    <div className={`w-full flex justify-center my-6 ${className}`} aria-hidden={loaded ? 'false' : 'true'}>
      <div className="max-w-2xl w-full" ref={containerRef}>
        {/* Reserve a predictable area for the ad to avoid late layout shifts/compositing */}
        <div
          className={`w-full inline-ad-placeholder ${loaded ? 'ad-loaded' : 'ad-loading'}`}
          style={{ minHeight: `${minH}px` }}
        >
          <SmartAd
            slot={slot}
            enabled={true}
            className="w-full h-full"
            style={{ minHeight: '100%' }}
          />
        </div>

        {/* Test-mode informational box still in-flow, but no absolute overlays or backdrop filters */}
        {config.ads.testMode && (
          <div className="mt-2 border-2 border-dashed border-blue-300 bg-blue-50 bg-opacity-90 rounded-lg p-4 text-center flex flex-col justify-center" style={{ minHeight: '120px' }}>
            <div className="text-blue-600 font-medium mb-2">
              📱 Advertisement Space ({slot})
            </div>
            <div className="text-xs text-blue-500">
              Provider: {config.ads.provider}
            </div>
            <div className="text-xs text-blue-400 mt-1">
              This box is informational in test mode.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InlineAd;
